import { useState, useCallback, useRef } from 'react';
import { parseSessionFile, processBulkSessions, generateBulkReport, type BulkSessionResult } from '@gpa-scales/shared-logic/engine/bulkProcessor';
import { useAppStore, recalculateStore } from '@/store/useAppStore';

interface ParsedPreview {
  fileName: string;
  courseCount: number;
  hasData: boolean;
}

export function BulkImport() {
  const [results, setResults] = useState<BulkSessionResult[]>([]);
  const [processing, setProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<ParsedPreview[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parsedRef = useRef<{ fileName: string; data: import('@gpa-scales/shared-types').SessionData }[]>([]);

  const parseOnly = useCallback(async (files: File[]) => {
    setProcessing(true);
    setResults([]);
    setPreview(null);

    const validSessions: { fileName: string; data: import('@gpa-scales/shared-types').SessionData }[] = [];
    const previews: ParsedPreview[] = [];

    for (const file of files) {
      try {
        const text = await file.text();
        const parsed = parseSessionFile(text, file.name);
        if (parsed) {
          validSessions.push(parsed);
          previews.push({
            fileName: file.name,
            courseCount: parsed.data.courses?.length ?? 0,
            hasData: true,
          });
        } else {
          previews.push({ fileName: file.name, courseCount: 0, hasData: false });
        }
      } catch {
        previews.push({ fileName: file.name, courseCount: 0, hasData: false });
      }
    }

    parsedRef.current = validSessions;
    setPreview(previews);
    setProcessing(false);
  }, []);

  const confirmImport = useCallback(() => {
    const bulkResults = processBulkSessions(parsedRef.current);
    setResults(bulkResults);
    setPreview(null);

    // Import all courses from valid sessions into the store
    for (const session of parsedRef.current) {
      if (session.data.courses) {
        useAppStore.getState().importCourses(session.data.courses);
      }
    }
    recalculateStore();
  }, []);

  const cancelPreview = useCallback(() => {
    setPreview(null);
    parsedRef.current = [];
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files).filter((f) => f.name.endsWith('.json'));
      if (files.length > 0) {
        parseOnly(files);
      }
    },
    [parseOnly],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
        ? Array.from(e.target.files).filter((f) => f.name.endsWith('.json'))
        : [];
      if (files.length > 0) {
        parseOnly(files);
      }
      // Reset so selecting the same file again still fires onChange
      e.target.value = '';
    },
    [parseOnly],
  );

  const handleExportBulkReport = useCallback(() => {
    if (results.length === 0) return;
    const csv = generateBulkReport(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpa-scales-bulk-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  const validCount = preview ? preview.filter((p) => p.hasData).length : 0;

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text)]">Bulk Import</h3>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragOver
            ? 'border-primary bg-primary-light'
            : 'border-border hover:border-primary'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <svg className="mx-auto h-8 w-8 text-[var(--color-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="mt-2 text-sm text-[var(--color-text-muted)]">
          Drag & drop JSON session files here, or click to browse
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">Supports multiple .json files</p>
      </div>

      {/* Processing indicator */}
      {processing && (
        <div className="mt-4 flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-[var(--color-text-muted)]">Processing files…</p>
        </div>
      )}

      {/* R26 — Preview step */}
      {preview && (
        <div className="mt-4 rounded-lg border border-border bg-[var(--color-bg-secondary)] p-4">
          <h4 className="mb-2 text-xs font-semibold text-[var(--color-text)]">Import Preview</h4>
          <div className="mb-3 space-y-1">
            {preview.map((p) => (
              <div key={p.fileName} className="flex items-center justify-between gap-2 text-xs">
                <span className="allow-shrink truncate text-[var(--color-text)]" title={p.fileName}>{p.fileName}</span>
                <span className={`shrink-0 ${p.hasData ? 'text-success' : 'text-error'}`}>
                  {p.hasData ? `${p.courseCount} courses` : 'Not a valid session file'}
                </span>
              </div>
            ))}
          </div>
          <p className="mb-3 text-xs text-[var(--color-text-muted)]">
            Import {validCount} session{validCount !== 1 ? 's' : ''}?
          </p>
          <div className="flex gap-2">
            <button
              onClick={confirmImport}
              disabled={validCount === 0}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
            >
              Import {validCount} Session{validCount !== 1 ? 's' : ''}
            </button>
            <button
              onClick={cancelPreview}
              className="rounded-md border border-border px-3 py-1.5 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-[var(--color-text)]">
              {results.length} session{results.length !== 1 ? 's' : ''} processed
            </p>
            <button
              onClick={handleExportBulkReport}
              className="rounded-md bg-primary px-3 py-1 text-xs text-white hover:bg-primary-hover"
            >
              Export CSV Report
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <caption className="sr-only">Bulk import results</caption>
              <thead>
                <tr className="border-b border-border text-left">
                  <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">File</th>
                  <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">Courses</th>
                  <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">UC Capped</th>
                  <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">Unweighted</th>
                  <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const ucCapped = r.result.variants.find((v) => v.label === 'UC Capped Weighted GPA');
                  const unweighted = r.result.variants.find((v) => v.label === 'Unweighted GPA');
                  return (
                    <tr key={i} className="border-b border-border">
                      <td className="allow-shrink max-w-[16rem] truncate px-2 py-1.5 text-[var(--color-text)]" title={r.fileName}>{r.fileName}</td>
                      <td className="px-2 py-1.5 text-[var(--color-text)]">{r.courseCount}</td>
                      <td className="px-2 py-1.5 text-[var(--color-text)]">
                        {ucCapped ? ucCapped.value.toFixed(3) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-[var(--color-text)]">
                        {unweighted ? unweighted.value.toFixed(3) : '—'}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.error ? (
                          <span className="font-medium text-error">{r.error}</span>
                        ) : (
                          <span className="font-medium text-success">Imported</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
